import { motion } from "motion/react";
import type { CSSProperties } from "react";
import { profile } from "@/data/profile";
import { t } from "@/lib/i18n";
import type { Lang } from "@/lib/i18n";
import type { PhaseControl } from "@/hooks/usePhase";
import { PhaseToggle } from "./PhaseToggle";

interface HeaderProps {
  lang: Lang;
  setLang: (l: Lang) => void;
  activeId: string;
  onSelect: (id: string) => void;
  phaseControl: PhaseControl;
}

const langBtn = (active: boolean): CSSProperties => ({
  background: "none",
  border: "none",
  cursor: "pointer",
  fontFamily: "var(--tc-mono)",
  fontSize: 12,
  letterSpacing: "0.12em",
  padding: 0,
  fontWeight: active ? 700 : 400,
  color: active ? "var(--tc-brass)" : "var(--tc-faint)",
});

export function Header({ lang, setLang, activeId, onSelect, phaseControl }: HeaderProps) {
  const { brand, sections } = profile;

  return (
    <header
      className="relative z-[5] flex flex-none flex-wrap items-center justify-between gap-4 px-6 py-4 md:gap-6 md:px-10 md:py-5"
      style={{ borderBottom: "1px solid var(--tc-line)" }}
    >
      {/* brand */}
      <div className="flex items-center gap-[11px]">
        <span
          style={{
            width: 30,
            height: 30,
            borderRadius: "50%",
            border: "1.5px solid var(--tc-brass)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontFamily: "var(--tc-mono)",
            fontSize: 11,
            fontWeight: 700,
            color: "var(--tc-brass)",
          }}
        >
          {brand.initials}
        </span>
        <span style={{ fontFamily: "var(--tc-serif)", fontSize: 21, color: "var(--tc-ink-strong)" }}>
          {brand.name}
          <span style={{ color: "var(--tc-brass)" }}>{brand.accent}</span>
        </span>
      </div>

      {/* nav */}
      <nav className="flex items-center gap-5 md:gap-[30px]">
        {sections.map((sec) => {
          const active = sec.id === activeId;
          return (
            <button
              key={sec.id}
              onClick={() => onSelect(sec.id)}
              aria-current={active ? "page" : undefined}
              style={{
                position: "relative",
                background: "none",
                border: "none",
                cursor: "pointer",
                fontFamily: "var(--tc-mono)",
                fontSize: 12,
                letterSpacing: "0.13em",
                textTransform: "uppercase",
                padding: "0 0 5px",
                transition: "color .25s ease",
                color: active ? "var(--tc-ink-strong)" : "var(--tc-faint)",
              }}
            >
              {t(sec.nav, lang)}
              {active && (
                <motion.span
                  layoutId="tc-nav-underline"
                  style={{
                    position: "absolute",
                    left: 0,
                    bottom: 0,
                    width: "100%",
                    height: 2,
                    background: "var(--tc-brass)",
                  }}
                  transition={{ type: "spring", stiffness: 380, damping: 30 }}
                />
              )}
            </button>
          );
        })}
      </nav>

      {/* time-of-day + language */}
      <div className="flex items-center gap-4">
        <PhaseToggle {...phaseControl} lang={lang} />
        <div
          className="flex items-center gap-[11px]"
          style={{ fontFamily: "var(--tc-mono)", fontSize: 12, letterSpacing: "0.12em" }}
        >
          <button onClick={() => setLang("pt")} style={langBtn(lang === "pt")} aria-pressed={lang === "pt"}>PT</button>
          <span style={{ color: "var(--tc-faint)" }}>/</span>
          <button onClick={() => setLang("en")} style={langBtn(lang === "en")} aria-pressed={lang === "en"}>EN</button>
        </div>
      </div>
    </header>
  );
}
